/**
 * "More from this fighter" (THEME.md §7.13; product page spec F3, F4; T-79, T-143): the element of
 * `sections/okt-fighter-row.liquid`. The section loads it on every row, so a row that the theme editor
 * re-renders with a "+N" card (scripts in re-rendered HTML don't run) still works.
 *
 * - The cards after Product count wait hidden: in a carousel their slides (`slideshow-slide[hidden]`,
 *   which the slideshow leaves out of its slides and still observes), in a grid the items themselves.
 *   Grid with Carousel on mobile renders the cards twice (the grid, and a carousel for phones); both wait.
 * - "+N" shows them in place, in both lists: the cards after it appear, every "+N" hides, and focus
 *   moves to the first card that appeared in the list that was clicked.
 */

const EXTRA = '[data-okt-fighter-extra]';
const MORE = '[data-okt-fighter-more]';

/**
 * The box to hide or show: the carousel slide around an item, else the grid item itself
 * @param {Element} element
 * @returns {HTMLElement | null}
 */
const boxOf = (element) => {
  const box = element.closest('slideshow-slide') ?? element.closest('.resource-list__item');
  return box instanceof HTMLElement ? box : null;
};

class OktFighterRow extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.#controller = new AbortController();

    if (this.dataset.expanded !== 'true') {
      for (const box of this.#extras(this)) box.hidden = true;
    }

    this.addEventListener('click', this.#handleClick, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  /**
   * @param {Element} root
   * @returns {HTMLElement[]}
   */
  #extras(root) {
    return [...root.querySelectorAll(EXTRA)].map(boxOf).filter((box) => box !== null);
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const more = event.target instanceof Element ? event.target.closest(MORE) : null;
    if (!(more instanceof HTMLElement)) return;

    const first = this.#extras(more.closest('.resource-list') ?? this)[0];

    this.dataset.expanded = 'true';
    for (const box of this.#extras(this)) box.hidden = false;
    for (const button of this.querySelectorAll(MORE)) {
      button.setAttribute('aria-expanded', 'true');
      const box = boxOf(button);
      if (box) box.hidden = true;
    }

    if (!first) return;

    // Horizon skips rendering slides it marks hidden from assistive tech; the first one is in view now
    if (first.matches('slideshow-slide')) first.setAttribute('aria-hidden', 'false');
    first.querySelector('a[href]')?.focus();
  };
}

if (!customElements.get('okt-fighter-row')) {
  customElements.define('okt-fighter-row', OktFighterRow);
}
